/**
 * Aanwezigheidskalender — grid (rijen × dagen) (Epic 4, Story 4.2, AC 1)
 *
 * Rijen = medewerkers, kolommen = dagen — het `dates`-array bepaalt de view
 * (dag = 1 datum, week = 5 werkdagen, maand = alle werkdagen van de
 * maand — het weekend is uit de weekweergave verdwenen), zodat één
 * grid-component alle drie views bedient (geen drie losse grid-
 * implementaties zoals `werkrooster`'s `PlanningWeekGrid`/`PlanningDayView`/
 * `PlanningMonthGrid` — hergebruikt hun interactiepatroon, niet hun code).
 */

import type { AttendanceCalendarResponse } from '@functieloonhuis/shared';
import { AanwezigheidCell } from './AanwezigheidCell';
import { CellErrorBoundary } from './CellErrorBoundary';
import { formatDateLabel } from '../../werkrooster/utils/dateUtils';

interface AanwezigheidGridProps {
  response: AttendanceCalendarResponse;
  dates: string[];
}

export function AanwezigheidGrid({ response, dates }: AanwezigheidGridProps) {
  if (response.employees.length === 0) {
    return (
      <div className="text-center py-6 text-sm text-gray-500" data-testid="aanwezigheid-grid-empty">
        Geen medewerkers gevonden.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table role="grid" data-testid="aanwezigheid-grid" className="w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className="text-left px-2 py-2 text-xs font-medium text-gray-500 sticky left-0 bg-white">Medewerker</th>
            {dates.map((date) => (
              <th key={date} className="px-1 py-2 text-[10px] font-medium text-gray-500 text-center min-w-[70px]">
                {formatDateLabel(new Date(`${date}T00:00:00`))}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {response.employees.map((employeeEntry) => (
            <tr key={employeeEntry.employee.id} className="border-t border-[#E2E8F0]">
              <td className="px-2 py-1 text-xs font-medium text-[#1E293B] sticky left-0 bg-white whitespace-nowrap">
                {/* Epic ux-aanwezigheidscel-duur, Story 1.3 (CAP-4, Open
                    Decision 1): the old always-visible row-level BHV-badge
                    is REMOVED — BHV-dekking now renders per-cel (see
                    AanwezigheidCell's isBhv prop below), only when the
                    person actually works that dagdeel. */}
                <span className="inline-flex items-center gap-1.5">{employeeEntry.employee.name}</span>
              </td>
              {dates.map((date) => {
                const day = employeeEntry.days.find((d) => d.date === date);
                if (!day) {
                  return <td key={date} className="px-1 py-1" />;
                }
                return (
                  <td key={date} className="px-1 py-1">
                    <CellErrorBoundary>
                      <AanwezigheidCell
                        employeeName={employeeEntry.employee.name}
                        morning={day.morning}
                        afternoon={day.afternoon}
                        holidayName={day.holidayName}
                        isBhv={employeeEntry.employee.isBhv}
                        kortVerlofUren={day.kortVerlofUren}
                        kortVerlofStart={day.kortVerlofStart}
                        kortVerlofEind={day.kortVerlofEind}
                      />
                    </CellErrorBoundary>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
